import React from "react";
import PropTypes from "prop-types";
import { Dialog, DialogContent, Tooltip } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import { ThreeDots } from "react-loader-spinner";

import cx from "classnames";
import { Clear } from "@material-ui/icons";
import Button from "../CustomButtons/Button";
import CustomInput from "../CustomInput/CustomInput";
import useThemeColors from "../../hooks/useThemeColors";
import ModalHeader from "../ModalHeader";

import {
  grayColor,
  dangerColor,
} from "assets/jss/material-dashboard-pro-react.js";

import modalStyle from "assets/jss/material-dashboard-pro-react/modalStyle";

const styles = (theme) => ({
  ...modalStyle(theme),
  modalRootHigher: {
    zIndex: "1400 !important",
  },
  commentsContainer: {
    maxHeight: "400px",
    overflowY: "auto",
    marginBottom: "20px",
  },
  commentCard: {
    borderRadius: "8px",
    padding: "12px",
    marginBottom: "10px",
    position: "relative",
  },
  commentHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "6px",
  },
  commentAuthor: {
    fontSize: "12px",
    fontWeight: "500",
    color: grayColor[2],
  },
  commentDate: {
    fontSize: "11px",
    color: grayColor[6],
  },
  commentActions: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  automaticComment: {
    fontSize: "12px",
    color: grayColor[3],
    fontStyle: "italic",
    marginBottom: "8px",
    paddingLeft: "8px",
  },
  userComment: {
    fontSize: "13px",
    color: grayColor[3],
    lineHeight: "1.5",
  },
  commentInputContainer: {
    marginTop: "15px",
  },
  commentHint: {
    fontSize: "12px",
    color: grayColor[6],
    marginTop: "8px",
    fontStyle: "italic",
  },
  dialogActions: {
    display: "flex",
    justifyContent: "center",
    padding: "16px",
    borderTop: `1px solid ${grayColor[10]}`,
  },
});

const CommentsDialog = ({
  open,
  setOpen,
  adjustment,
  newComment,
  setNewComment,
  onAddComment,
  onDeleteComment,
  currentUserId,
  parseComment,
  saving,
}) => {
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const { t } = useTranslation();
  const { clientColor, clientTextColor } = useThemeColors();

  if (!adjustment) return null;

  return (
    <Dialog
      classes={{
        root: cx(classes.modalRoot, classes.modalRootHigher),
        paper: classes.modal,
      }}
      open={open}
      keepMounted
      onClose={() => !saving && setOpen(false)}
      aria-labelledby="comments-dialog-title"
      fullWidth
      maxWidth={"sm"}
    >
      <ModalHeader
        id="comments-dialog-title"
        title={t("adjustments.comments")}
        subtitle={adjustment.description}
        pills={
          adjustment.comments?.length
            ? [
                {
                  key: "comments",
                  label: t("adjustments.comments_count", {
                    count: adjustment.comments.length,
                  }),
                },
              ]
            : []
        }
        onClose={() => !saving && setOpen(false)}
        closeDisabled={saving}
        closeLabel={t("common.close")}
        clientColor={clientColor}
      />
      <DialogContent className={classes.modalBody}>
        {/* Comments History */}
        {adjustment.comments && adjustment.comments.length > 0 && (
          <div className={classes.commentsContainer}>
            {adjustment.comments.map((comment, index) => {
              const parsed = parseComment(comment.comment);
              const canDelete =
                Number(comment.created_by_id) === Number(currentUserId);

              return (
                <div
                  key={index}
                  className={classes.commentCard}
                  style={{
                    backgroundColor: `${clientColor}08`,
                    border: `1px solid ${clientColor}22`,
                  }}
                >
                  <div className={classes.commentHeader}>
                    <span className={classes.commentAuthor}>
                      {comment.created_by}
                    </span>
                    <div className={classes.commentActions}>
                      <span className={classes.commentDate}>
                        {comment.created_at.split(" ")[0]}
                      </span>
                      {canDelete && (
                        <Tooltip
                          title={t("adjustments.delete_comment")}
                          arrow
                          enterDelay={500}
                        >
                          <Clear
                            style={{
                              fontSize: "18px",
                              cursor: "pointer",
                              color: grayColor[6],
                              transition: "color 0.2s",
                            }}
                            onMouseEnter={(e) =>
                              (e.target.style.color = dangerColor[0])
                            }
                            onMouseLeave={(e) =>
                              (e.target.style.color = grayColor[6])
                            }
                            onClick={() => onDeleteComment(comment.id)}
                          />
                        </Tooltip>
                      )}
                    </div>
                  </div>

                  {parsed.hasAutomatic && (
                    <div
                      className={classes.automaticComment}
                      style={{
                        borderLeft: `3px solid ${parsed.isRejection ? dangerColor[0] : clientColor}`,
                      }}
                    >
                      {parsed.automaticText}
                    </div>
                  )}

                  {parsed.userComment && (
                    <div className={classes.userComment}>
                      {parsed.userComment}
                    </div>
                  )}

                  {!parsed.hasAutomatic && !parsed.userComment && (
                    <div className={classes.userComment}>{comment.comment}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Add New Comment */}
        <div className={classes.commentInputContainer}>
          <CustomInput
            id="new_comment"
            labelText={t("adjustments.add_comment_optional")}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            formControlProps={{
              style: {
                width: "100%",
                marginTop: "-10px",
              },
            }}
            inputProps={{
              multiline: true,
              rows: 3,
              autoComplete: "off",
              disabled: saving,
              placeholder: t("adjustments.add_comment_optional_placeholder"),
            }}
            client
          />
        </div>
      </DialogContent>
      <div className={classes.dialogActions}>
        <Button
          onClick={onAddComment}
          color="client"
          disabled={saving || !newComment.trim()}
        >
          {saving ? (
            <ThreeDots
              height="18px"
              width="18px"
              color={clientTextColor}
              ariaLabel="loading"
              wrapperStyle={{}}
              wrapperClass=""
            />
          ) : (
            t("adjustments.add_comment")
          )}
        </Button>
      </div>
    </Dialog>
  );
};

CommentsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  adjustment: PropTypes.object,
  newComment: PropTypes.string.isRequired,
  setNewComment: PropTypes.func.isRequired,
  onAddComment: PropTypes.func.isRequired,
  onDeleteComment: PropTypes.func.isRequired,
  currentUserId: PropTypes.number,
  parseComment: PropTypes.func.isRequired,
  saving: PropTypes.bool,
};

export default CommentsDialog;
